import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  SyncOutlined
} from "@ant-design/icons"
import { Alert, Button, Empty, Flex, Result, Spin, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { RoutesPaths } from "../../constants/routes.ts"

const { Text } = Typography

/**
 * Six pure, presentational states for the Global Map surface — no Redux, no
 * API calls, no router. Task 20 wires these into the live page against real
 * fetch/staleness state; here they only take the props they need to render.
 */

export function MapLoading() {
  const { t } = useTranslation()

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={8}
      role="status"
      aria-live="polite"
      className="min-h-[220px] px-6 py-6 text-center"
    >
      <Spin size="large" />
      <Text type="secondary">{t("globalMap.states.loading")}</Text>
    </Flex>
  )
}

interface MapNoResultsProps {
  onReset?: () => void
}

export function MapNoResults({ onReset }: MapNoResultsProps) {
  const { t } = useTranslation()

  return (
    <Empty
      description={
        <Text type="secondary" strong>
          {t("globalMap.states.noResults.title")}
        </Text>
      }
    >
      {onReset && (
        <Button onClick={onReset}>
          {t("globalMap.states.noResults.reset")}
        </Button>
      )}
    </Empty>
  )
}

export function MapNoData() {
  const { t } = useTranslation()

  return (
    <Empty
      description={
        <Flex vertical align="center" className="text-center py-1">
          <Text type="secondary" strong>
            {t("globalMap.states.noData.title")}
          </Text>
          <Text type="secondary" className="mt-1 max-w-xs">
            {t("globalMap.states.noData.description")}
          </Text>
        </Flex>
      }
    />
  )
}

interface MapPartialGeocodingProps {
  unmapped: number
  onViewRecords?: () => void
}

// Stub target for this increment — Task 20's live page decides whether this
// should deep-link with the map's current filters instead of the bare list.
export function MapPartialGeocoding({
  unmapped,
  onViewRecords
}: MapPartialGeocodingProps) {
  const { t } = useTranslation()

  return (
    <Alert
      type="warning"
      showIcon
      message={t("globalMap.states.partialGeocoding.title", {
        count: unmapped
      })}
      action={
        <a href={RoutesPaths.project.list} onClick={onViewRecords}>
          {t("globalMap.states.partialGeocoding.viewRecords")}
        </a>
      }
    />
  )
}

interface MapStaleProps {
  onRefresh: () => void
}

export function MapStale({ onRefresh }: MapStaleProps) {
  const { t } = useTranslation()

  return (
    <Alert
      type="info"
      showIcon
      message={t("globalMap.states.stale.title")}
      action={
        <Button size="small" icon={<SyncOutlined />} onClick={onRefresh}>
          {t("globalMap.states.stale.refresh")}
        </Button>
      }
    />
  )
}

interface MapProviderErrorProps {
  onRetry: () => void
}

// Renders in place of the map only — the rest of the page (sidebar, filters)
// stays mounted and usable around it.
export function MapProviderError({ onRetry }: MapProviderErrorProps) {
  const { t } = useTranslation()

  return (
    <Result
      status="warning"
      icon={<ExclamationCircleOutlined />}
      title={t("globalMap.states.providerError.title")}
      extra={
        <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
          {t("globalMap.states.providerError.retry")}
        </Button>
      }
    />
  )
}
